import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    X,
    Search,
    Shield,
    ShieldCheck,
    ShieldAlert,
    Cpu,
    CheckSquare,
    Square,
    ArrowRight,
    Loader2,
    AlertCircle,
    CheckCircle2,
    XCircle,
    RefreshCw,
    RotateCcw,
} from "lucide-react";
import {
    createAccessGroup,
    updateAccessGroup,
    lookupDevices,
    saveAccessGroupScopes,
} from "../services/accessGroupApi";

const EFFECT = { Deny: 0, Allow: 1 };

// "Deny"/"Allow" | 0/1 | null → 0/1'e çevir
function normEffect(v, fallback = EFFECT.Deny) {
    if (v === null || v === undefined) return fallback;
    if (typeof v === "number") return v;
    if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        if (s === "deny" || s === "denied" || s === "yasak" || s === "yasaklı") return EFFECT.Deny;
        if (s === "allow" || s === "allowed" || s === "yetkili" || s === "izin") return EFFECT.Allow;
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
    }
    return fallback;
}

// Helpers
function getUniqueKey(d) {
    if (d.key) return d.key;
    return String(d.id); // Fallback for old/simple devices
}

function contains(list, key) {
    return list.some((x) => getUniqueKey(x) === key);
}

function deviceLabel(d) {
    return `${d.name ?? ""}${d.ip ? ` (${d.ip})` : ""} — #${d.id}`;
}

export default function CreateAccessGroupModal({
    open,
    onClose,
    onSaved,
    canCreate = true,
    canUpdate = true,
    mode = "create",
    groupId = null,
    initialGroup = null, // { id,name,description,isActive,defaultEffect,scopes:[{deviceId,effect,...}] }
}) {
    // Üst alanlar
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [defaultEffect, setDefaultEffect] = useState(EFFECT.Deny);

    // Sol havuz
    const [q, setQ] = useState("");
    const [pool, setPool] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPoolKeys, setSelectedPoolKeys] = useState([]); // Store Keys, not IDs

    // Sağ kovalar
    const [allowed, setAllowed] = useState([]); // Array of full device/slot objects
    const [denied, setDenied] = useState([]);   // Array of full device/slot objects

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // modal açılışında state’i hazırla
    useEffect(() => {
        if (!open) return;

        if (mode === "edit" && initialGroup) {
            setName(initialGroup.name ?? "");
            setDescription(initialGroup.description ?? "");
            setIsActive(!!initialGroup.isActive);
            setDefaultEffect(normEffect(initialGroup.defaultEffect, EFFECT.Deny));

            // initial scopes → allowed/denied kovaları
            const alw = [];
            const dny = [];
            (initialGroup.scopes || []).forEach((s) => {
                const eff = normEffect(s.effect, EFFECT.Allow);

                // Reconstruct a "Slot-Like" or "Device-Like" object from scope
                let itemKey = `D_${s.deviceId}`;
                let isSlot = false;
                let displayName = s.deviceName || s.device?.name || "";

                // If scope has readerNo, treat as slot (or specific reader)
                if (s.readerNo) {
                    itemKey = `S_${s.deviceId}_${s.readerNo}`;
                    isSlot = true;
                    // Fix Label for Edit Mode
                    const dirStr = s.isEntry === true ? "Giriş" : (s.isEntry === false ? "Çıkış" : "");
                    displayName = `${displayName} - Slot ${s.readerNo}${dirStr ? `: (${dirStr})` : ""}`;
                }

                const stub = {
                    key: itemKey,
                    id: s.deviceId,
                    deviceId: s.deviceId,
                    name: displayName,
                    ip: s.deviceIp || s.device?.ip || "",
                    readerNo: s.readerNo,
                    isEntry: s.isEntry,
                    isPanelSlot: isSlot,
                };

                if (eff === EFFECT.Deny) dny.push(stub);
                else alw.push(stub);
            });
            setAllowed(mergeUniqueByKey(alw));
            setDenied(mergeUniqueByKey(dny));
        } else {
            // create
            setName("");
            setDescription("");
            setIsActive(true);
            setDefaultEffect(EFFECT.Deny);
            setAllowed([]);
            setDenied([]);
        }

        setQ("");
        setPool([]);
        setSelectedPoolKeys([]);
        setSaving(false);
        setError("");
    }, [open, mode, initialGroup]);

    // Havuz (sol liste) yükle
    const loadPool = useCallback(async () => {
        setLoading(true);
        try {
            const list = await lookupDevices(q);
            let arr = Array.isArray(list) ? list : [];

            // Filter out Panel Parent Rows
            arr = arr.filter((d) => !d.isPanel);

            // Filter out items already in allowed/denied
            const blockKeys = new Set([...allowed, ...denied].map((d) => getUniqueKey(d)));
            arr = arr.filter((d) => !blockKeys.has(getUniqueKey(d)));

            setPool(arr);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [q, allowed, denied]);

    // modal açılınca ilk yükleme
    useEffect(() => {
        if (!open) return;
        loadPool();
    }, [open, loadPool]);

    // kovalar değiştiğinde pool’u tekrar süz
    useEffect(() => {
        if (!open) return;
        loadPool();
    }, [allowed, denied, open, loadPool]);

    const canSubmit = useMemo(() => {
        const nameOk = name.trim().length > 0;
        return mode === "create" ? canCreate && nameOk : canUpdate && nameOk;
    }, [mode, canCreate, canUpdate, name]);

    // listeler arası taşıma
    const togglePoolSelect = (key) => {
        setSelectedPoolKeys((prev) =>
            prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
        );
    };

    const toggleSelectAllPool = () => {
        if (pool.length === 0) return;
        if (selectedPoolKeys.length === pool.length) {
            setSelectedPoolKeys([]);
        } else {
            setSelectedPoolKeys(pool.map((d) => getUniqueKey(d)));
        }
    };

    const moveSelectedTo = (target /* "allow" | "deny" */) => {
        if (selectedPoolKeys.length === 0) return;
        const moving = pool.filter((d) => selectedPoolKeys.includes(getUniqueKey(d)));
        const rest = pool.filter((d) => !selectedPoolKeys.includes(getUniqueKey(d)));
        setPool(rest);
        setSelectedPoolKeys([]);

        if (target === "allow") {
            const deniedRest = denied.filter((d) => !contains(moving, getUniqueKey(d)));
            setDenied(deniedRest);
            setAllowed(mergeUniqueByKey([...allowed, ...moving]));
        } else {
            const allowedRest = allowed.filter((d) => !contains(moving, getUniqueKey(d)));
            setAllowed(allowedRest);
            setDenied(mergeUniqueByKey([...denied, ...moving]));
        }
    };

    const removeFromAllowed = (key) => {
        const item = allowed.find((x) => getUniqueKey(x) === key);
        setAllowed((prev) => prev.filter((x) => getUniqueKey(x) !== key));
        if (item) setPool((prev) => [item, ...prev]);
    };

    const removeFromDenied = (key) => {
        const item = denied.find((x) => getUniqueKey(x) === key);
        setDenied((prev) => prev.filter((x) => getUniqueKey(x) !== key));
        if (item) setPool((prev) => [item, ...prev]);
    };

    const clearAllAllowed = () => {
        if (allowed.length === 0) return;
        setPool((prev) => [...allowed, ...prev]);
        setAllowed([]);
    };

    const clearAllDenied = () => {
        if (denied.length === 0) return;
        setPool((prev) => [...denied, ...prev]);
        setDenied([]);
    };

    // Kaydet
    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;
        setSaving(true);
        setError("");

        try {
            if (mode === "create") {
                const createRes = await createAccessGroup({
                    name: name.trim(),
                    description: (description || "").trim(),
                    isActive,
                    defaultEffect,
                });
                const newId =
                    createRes?.id || createRes?.Id ||
                    createRes?.data?.id || createRes?.data?.Id;
                if (!newId) throw new Error("Grup ID alınamadı.");

                const payload = buildScopesPayload(allowed, denied);
                await saveAccessGroupScopes(newId, payload);
            } else {
                if (!groupId) throw new Error("groupId bulunamadı.");
                await updateAccessGroup(groupId, {
                    name: name.trim(),
                    description: (description || "").trim(),
                    isActive,
                    defaultEffect,
                });
                const payload = buildScopesPayload(allowed, denied);
                await saveAccessGroupScopes(groupId, payload);
            }

            onSaved?.();
            onClose?.();
        } catch (err) {
            console.error(err);
            setError(err?.message || "Kaydetme sırasında hata oluştu.");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm transition-opacity"
                onClick={() => (!saving ? onClose?.() : null)}
            />

            {/* Modal Container */}
            <div className="relative w-full max-w-6xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 overflow-hidden flex flex-col max-h-[92vh] z-10">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/70 bg-gradient-to-r from-gray-50/80 via-white to-indigo-50/30 dark:from-gray-800 dark:via-gray-800 dark:to-indigo-950/20 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-sm">
                            <Shield className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                                {mode === "create" ? "Yeni Yetki Grubu Oluştur" : "Yetki Grubunu Düzenle"}
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Gruba bağlı personellerin hangi cihaz ve kapılardan geçiş yapabileceğini belirleyin.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                        onClick={() => (!saving ? onClose?.() : null)}
                        title="Kapat"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form Body */}
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                    <div className="p-6 space-y-5 overflow-y-auto flex-1">
                        {error && (
                            <div className="flex items-center gap-3 px-4 py-3 text-sm rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border border-rose-200/80 dark:border-rose-800/50">
                                <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
                                <span className="font-medium">{error}</span>
                            </div>
                        )}

                        {/* Top Form Fields Card */}
                        <div className="rounded-2xl bg-gray-50/70 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/70 p-4 sm:p-5">
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                {/* Grup Adı */}
                                <div className="md:col-span-3">
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">
                                        Grup Adı <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                                        placeholder="Örn: Genel Merkez Personeli"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                </div>

                                {/* Açıklama */}
                                <div className="md:col-span-4">
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">
                                        Açıklama
                                    </label>
                                    <input
                                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                                        placeholder="Yetki grubu hakkında kısa açıklama..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>

                                {/* Varsayılan Etki */}
                                <div className="md:col-span-3">
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">
                                        Varsayılan Etki
                                    </label>
                                    <select
                                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                                        value={defaultEffect}
                                        onChange={(e) => setDefaultEffect(Number(e.target.value))}
                                    >
                                        <option value={EFFECT.Deny}>Liste dışı: GEÇEMEZ (Önerilen)</option>
                                        <option value={EFFECT.Allow}>Liste dışı: GEÇEBİLİR</option>
                                    </select>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                        Bu grupta listelenmeyen cihazlar için varsayılan davranış.
                                    </p>
                                </div>

                                {/* Aktif / Pasif Durumu */}
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">
                                        Durum
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setIsActive((prev) => !prev)}
                                        className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                                            isActive
                                                ? "bg-emerald-50/80 dark:bg-emerald-900/25 border-emerald-200 dark:border-emerald-800/60 text-emerald-700 dark:text-emerald-300"
                                                : "bg-gray-100/80 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400"
                                        }`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span
                                                className={`w-2 h-2 rounded-full ${
                                                    isActive ? "bg-emerald-500" : "bg-gray-400"
                                                }`}
                                            />
                                            {isActive ? "Aktif" : "Pasif"}
                                        </span>
                                        <input
                                            id="isActive"
                                            type="checkbox"
                                            className="sr-only"
                                            checked={isActive}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                        />
                                        <div
                                            className={`w-8 h-4.5 flex items-center rounded-full p-0.5 transition-colors ${
                                                isActive ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"
                                            }`}
                                        >
                                            <div
                                                className={`bg-white w-3.5 h-3.5 rounded-full shadow-sm transform transition-transform ${
                                                    isActive ? "translate-x-3.5" : "translate-x-0"
                                                }`}
                                            />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* 3-Column Device Scope Assignment */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            {/* Column 1: Sol Havuz */}
                            <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800/90 shadow-sm flex flex-col overflow-hidden">
                                {/* Column Header */}
                                <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700/70 bg-gray-50/70 dark:bg-gray-900/40 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                                            <Cpu className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                                Cihaz Havuzu
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200/80 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                    {pool.length}
                                                </span>
                                            </h3>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                Sağdaki listelerde yer almayan cihazlar
                                            </p>
                                        </div>
                                    </div>

                                    {pool.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={toggleSelectAllPool}
                                            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 px-2 py-1 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                        >
                                            {selectedPoolKeys.length === pool.length ? "Seçimi Kaldır" : "Tümünü Seç"}
                                        </button>
                                    )}
                                </div>

                                {/* Search Bar */}
                                <div className="p-3 border-b border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800">
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-1">
                                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                            <input
                                                placeholder="Cihaz adı veya IP ara…"
                                                className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/60 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                                                value={q}
                                                onChange={(e) => setQ(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        loadPool();
                                                    }
                                                }}
                                            />
                                            {q && (
                                                <button
                                                    type="button"
                                                    onClick={() => setQ("")}
                                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={loadPool}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                            title="Listeyi Yenile"
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                                            Ara
                                        </button>
                                    </div>
                                </div>

                                {/* Pool List */}
                                <div className="h-72 overflow-y-auto p-3 space-y-2 flex-1">
                                    {loading ? (
                                        <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 gap-2">
                                            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                                            <span className="text-xs">Cihaz havuzu yükleniyor…</span>
                                        </div>
                                    ) : pool.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-4 text-gray-400 dark:text-gray-500">
                                            <Cpu className="w-8 h-8 mb-2 opacity-40" />
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                                Havuzda gösterilecek cihaz yok
                                            </p>
                                            <p className="text-[11px] mt-0.5">
                                                Tüm cihazlar sağdaki listelere aktarılmış veya arama kriterine uygun cihaz bulunamadı.
                                            </p>
                                        </div>
                                    ) : (
                                        pool.map((d) => {
                                            const key = getUniqueKey(d);
                                            const isSelected = selectedPoolKeys.includes(key);
                                            return (
                                                <label
                                                    key={key}
                                                    className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-all select-none ${
                                                        isSelected
                                                            ? "bg-indigo-50/80 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 shadow-sm"
                                                            : "bg-gray-50/50 dark:bg-gray-900/30 border-gray-200/70 dark:border-gray-700/60 hover:bg-gray-100/70 dark:hover:bg-gray-700/40"
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only"
                                                        checked={isSelected}
                                                        onChange={() => togglePoolSelect(key)}
                                                    />
                                                    {isSelected ? (
                                                        <CheckSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                                                    ) : (
                                                        <Square className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                                            {d.name || `Cihaz #${d.id}`}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                                                            {d.ip && <span>IP: {d.ip}</span>}
                                                            <span>#{d.id}</span>
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Pool Footer Actions */}
                                <div className="p-3 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50/70 dark:bg-gray-900/40 flex gap-2">
                                    <button
                                        type="button"
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:opacity-45 disabled:pointer-events-none transition-all"
                                        disabled={selectedPoolKeys.length === 0}
                                        onClick={() => moveSelectedTo("allow")}
                                    >
                                        <span>Seçilenleri Yetkili</span>
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-rose-600 hover:bg-rose-700 text-white shadow-sm disabled:opacity-45 disabled:pointer-events-none transition-all"
                                        disabled={selectedPoolKeys.length === 0}
                                        onClick={() => moveSelectedTo("deny")}
                                    >
                                        <span>Seçilenleri Yasaklı</span>
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>

                            {/* Column 2: Orta — Yetkili (Allow) */}
                            <div className="rounded-2xl border border-emerald-200/80 dark:border-emerald-800/50 bg-emerald-50/20 dark:bg-emerald-950/10 shadow-sm flex flex-col overflow-hidden">
                                <div className="px-4 py-3.5 border-b border-emerald-200/60 dark:border-emerald-800/50 bg-emerald-50/60 dark:bg-emerald-900/20 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                            <ShieldCheck className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                                Yetkili Cihazlar
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300">
                                                    {allowed.length}
                                                </span>
                                            </h3>
                                            <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80">
                                                Geçiş izni verilen cihaz ve kapılar
                                            </p>
                                        </div>
                                    </div>

                                    {allowed.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={clearAllAllowed}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/40 px-2 py-1 rounded-lg transition-colors"
                                            title="Tümünü Havuza Geri Al"
                                        >
                                            <RotateCcw className="w-3 h-3" />
                                            Temizle
                                        </button>
                                    )}
                                </div>

                                <div className="h-80 overflow-y-auto p-3 space-y-2 flex-1">
                                    {allowed.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-4 text-gray-400 dark:text-gray-500">
                                            <CheckCircle2 className="w-8 h-8 mb-2 text-emerald-400/50" />
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                                Henüz yetkili cihaz eklenmedi
                                            </p>
                                            <p className="text-[11px] mt-0.5">
                                                Soldaki havuzdan cihaz seçip &ldquo;Seçilenleri Yetkili&rdquo; butonuna tıklayın.
                                            </p>
                                        </div>
                                    ) : (
                                        allowed.map((d) => {
                                            const key = getUniqueKey(d);
                                            return (
                                                <div
                                                    key={key}
                                                    className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200/90 dark:border-emerald-800/60 bg-white dark:bg-gray-800/90 px-3 py-2.5 shadow-sm"
                                                >
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                        <div className="min-w-0">
                                                            <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                                                {deviceLabel(d)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="p-1.5 rounded-lg text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors shrink-0"
                                                        onClick={() => removeFromAllowed(key)}
                                                        title="Havuza geri al"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Column 3: Sağ — Yasaklı (Deny) */}
                            <div className="rounded-2xl border border-rose-200/80 dark:border-rose-800/50 bg-rose-50/20 dark:bg-rose-950/10 shadow-sm flex flex-col overflow-hidden">
                                <div className="px-4 py-3.5 border-b border-rose-200/60 dark:border-rose-800/50 bg-rose-50/60 dark:bg-rose-900/20 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                                            <ShieldAlert className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                                Yasaklı Cihazlar
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300">
                                                    {denied.length}
                                                </span>
                                            </h3>
                                            <p className="text-[11px] text-rose-700/80 dark:text-rose-300/80">
                                                Geçişi açıkça engellenen cihazlar
                                            </p>
                                        </div>
                                    </div>

                                    {denied.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={clearAllDenied}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 dark:text-rose-300 hover:bg-rose-100/80 dark:hover:bg-rose-900/40 px-2 py-1 rounded-lg transition-colors"
                                            title="Tümünü Havuza Geri Al"
                                        >
                                            <RotateCcw className="w-3 h-3" />
                                            Temizle
                                        </button>
                                    )}
                                </div>

                                <div className="h-80 overflow-y-auto p-3 space-y-2 flex-1">
                                    {denied.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-4 text-gray-400 dark:text-gray-500">
                                            <XCircle className="w-8 h-8 mb-2 text-rose-400/50" />
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                                Henüz yasaklı cihaz eklenmedi
                                            </p>
                                            <p className="text-[11px] mt-0.5">
                                                Soldaki havuzdan cihaz seçip &ldquo;Seçilenleri Yasaklı&rdquo; butonuna tıklayın.
                                            </p>
                                        </div>
                                    ) : (
                                        denied.map((d) => {
                                            const key = getUniqueKey(d);
                                            return (
                                                <div
                                                    key={key}
                                                    className="flex items-center justify-between gap-2 rounded-xl border border-rose-200/90 dark:border-rose-800/60 bg-white dark:bg-gray-800/90 px-3 py-2.5 shadow-sm"
                                                >
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
                                                        <div className="min-w-0">
                                                            <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                                                {deviceLabel(d)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="p-1.5 rounded-lg text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors shrink-0"
                                                        onClick={() => removeFromDenied(key)}
                                                        title="Havuza geri al"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sticky Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50/70 dark:bg-gray-900/40 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3">
                            <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <strong className="text-gray-700 dark:text-gray-200">{allowed.length}</strong> Yetkili
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-rose-500" />
                                <strong className="text-gray-700 dark:text-gray-200">{denied.length}</strong> Yasaklı
                            </span>
                        </div>

                        <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
                            <button
                                type="button"
                                onClick={() => onClose?.()}
                                className="px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                disabled={saving}
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-sm shadow-indigo-500/20 disabled:opacity-50 disabled:pointer-events-none transition-all"
                                disabled={!canSubmit || saving}
                            >
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                {mode === "create" ? "Oluştur" : "Güncelle"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function mergeUniqueByKey(arr) {
    const map = new Map();
    arr.forEach((x) => map.set(getUniqueKey(x), x));
    return Array.from(map.values());
}

function buildScopesPayload(allowed, denied) {
    const mapItem = (d, effect) => {
        return {
            deviceId: Number(d.deviceId || d.id),
            readerNo: d.readerNo || null,
            doorNo: d.readerNo || null,
            isEntry: d.isEntry === true ? true : (d.isEntry === false ? false : null),
            effect: effect,
        };
    };

    return [
        ...allowed.map((d) => mapItem(d, EFFECT.Allow)),
        ...denied.map((d) => mapItem(d, EFFECT.Deny)),
    ];
}
